import React from "react";
import { useLocation, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import SEO, { buildServiceJsonLd, buildBreadcrumbJsonLd } from "@/components/SEO";
import { PageHeader, Section } from "@/components/Primitives";
import QuoteForm from "@/components/QuoteForm";
import { SERVICES, CITIES, NEARBY, SC_META, BUSINESS, scSlug } from "@/data/config";
import { motion as m } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";

// Unique per-combo intro copy, avoiding city-swap templating.
function makeIntro(service, city) {
  const svcMap = {
    "gutter-installation": `A new gutter run in ${city.name} is not a one line quote. We look at pitch, valley count, downspout drop, and the trees that shed on your roof. Most ${city.name} homes go up in a single day, cut to length in the driveway, and hung with hidden hangers every 24 inches so nothing sags after the first heavy snow.`,
    "gutter-repair": `${city.name} gutter repair usually starts with a phone call after the first freeze. Miters leak. Hangers pull. Downspouts crack at the elbow. We come out, ladder up, and give a flat repair price the same visit. Most single story homes in ${city.name} are finished in an afternoon.`,
    "gutter-cleaning": `Gutter cleaning in ${city.name} is a hand job. No leaf blowers into flower beds. A two person crew clears every run, bags the debris, and flushes every downspout with a hose. You get before and after photos when the truck pulls out of your ${city.name} driveway.`,
    "gutter-guards": `Gutter guards in ${city.name} live or die on the mesh. Cheap plastic screens warp in July. Foam inserts hold water. We install stainless micro mesh at .020 opening, pitched to shed the pine needles ${city.name} throws at your roof from August through November.`,
    roofing: `Roofing in ${city.name} lives under real weather. Snow load in January. Wildfire smoke in August. UV twelve months a year. We install architectural asphalt shingles and 24 gauge standing seam metal roofs engineered for what ${city.name} actually sees.`,
  };
  return svcMap[service.slug];
}

function makeLocalNotes(service, city) {
  return [
    `${city.name} weather note: ${city.weatherAngle}`,
    `Nearby neighborhoods we cover: ${city.neighborhoods.slice(0, 6).join(", ")}.`,
    `Typical ${city.name} home style: ${
      city.slug === "spokane"
        ? "craftsman, mid century ranch, and modern build. Fascia depth varies block to block."
        : city.slug === "spokane-valley"
        ? "single story ranch on wide lots, plus newer two story builds in Greenacres and Ponderosa."
        : city.slug === "liberty-lake"
        ? "modern lake homes and River District new construction with clean roof lines."
        : city.slug === "mead"
        ? "one and two story homes surrounded by ponderosa pine. Needle load is heavy year round."
        : city.slug === "cheney"
        ? "college rentals, farm rebuilds, and newer split levels near EWU."
        : "rural properties with long roof runs, dormers, and porch tie ins."
    }`,
  ];
}

export default function ServiceCity() {
  const { pathname } = useLocation();
  const comboSlug = pathname.replace(/^\//, "").replace(/\/$/, "");
  const combo = SERVICES.flatMap((s) =>
    CITIES.map((c) => ({ s, c, slug: scSlug(c.slug, s.slug) }))
  ).find((x) => x.slug === comboSlug);
  if (!combo) return <Navigate to="/" replace />;
  const { s: service, c: city } = combo;
  const meta = SC_META[comboSlug] || {};

  const title = meta.title || `${city.name} ${service.name} | Spokane Gutter Co.`;
  const description =
    meta.meta ||
    `${city.name} ${service.name}. Local crew, on site quote, one year workmanship warranty. Call ${BUSINESS.phone}.`;
  const h1 = meta.h1 || `${city.name} ${service.name}.`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      buildServiceJsonLd(service, city),
      buildBreadcrumbJsonLd([
        { name: "Home", path: "/" },
        { name: service.name, path: `/${service.slug}` },
        { name: `${city.name} ${service.name}`, path: `/${comboSlug}` },
      ]),
    ],
  };

  const intro = makeIntro(service, city);
  const notes = makeLocalNotes(service, city);
  const nearbySlugs = NEARBY[city.slug] || [];
  const nearbyCombos = nearbySlugs.map((cs) => ({
    citySlug: cs,
    city: CITIES.find((x) => x.slug === cs),
    slug: scSlug(cs, service.slug),
  }));

  return (
    <>
      <SEO title={title} description={description} path={`/${comboSlug}`} jsonLd={jsonLd} />
      <PageHeader overline={`${service.accent} · ${city.accent}`} title={h1} lede={intro} />

      <Section testid="sc-body">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <p className="overline mb-6">Local notes</p>
            <ul className="space-y-6 mb-14">
              {notes.map((n, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="border-l-2 border-copper-500 pl-6 text-bone-100 text-lg leading-relaxed"
                >
                  {n}
                </motion.li>
              ))}
            </ul>

            <h2 className="font-display text-3xl md:text-4xl tracking-tightest mb-6">
              What our {city.name} {service.name.toLowerCase()} job includes.
            </h2>
            <ul className="space-y-3 mb-14">
              {service.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3 border-b border-white/5 pb-3">
                  <Check className="w-5 h-5 text-copper-500 mt-1 shrink-0" />
                  <span className="text-bone-100">{b}</span>
                </li>
              ))}
            </ul>

            <h2 className="font-display text-3xl md:text-4xl tracking-tightest mb-6">
              How a {city.name} {service.name.toLowerCase()} job runs.
            </h2>
            <ol className="space-y-6 mb-14">
              {service.process.map((p, i) => (
                <li key={i} className="grid grid-cols-[auto_1fr] gap-6 items-start">
                  <span className="font-display text-3xl md:text-4xl text-copper-500 leading-none">
                    0{i + 1}
                  </span>
                  <span className="text-bone-100 text-lg">{p}</span>
                </li>
              ))}
            </ol>

            <div className="border-t border-white/10 pt-8 space-y-3 text-bone-300">
              <p>
                Want the full {service.name.toLowerCase()} overview? Read{" "}
                <Link to={`/${service.slug}`} className="text-copper-500 hover:underline">
                  {service.name} in Spokane WA
                </Link>
                . For everything we do in this city, see{" "}
                <Link to={`/${city.slug}`} className="text-copper-500 hover:underline">
                  {city.name} gutters and roofing
                </Link>
                .
              </p>
            </div>
          </div>

          <div className="md:col-span-5">
            <div className="sticky top-28 space-y-6">
              <div className="border border-white/10 bg-ink-900 rounded-sm p-6">
                <p className="overline mb-3">City fast facts</p>
                <dl className="text-sm space-y-3">
                  <div className="flex justify-between border-b border-white/5 pb-2">
                    <dt className="text-bone-400">Zip</dt>
                    <dd className="text-bone-100">{city.zip}</dd>
                  </div>
                  <div className="flex justify-between border-b border-white/5 pb-2">
                    <dt className="text-bone-400">Service radius</dt>
                    <dd className="text-bone-100">{city.name} + 10 mi</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-bone-400">Typical response</dt>
                    <dd className="text-bone-100">Within 1 business day</dd>
                  </div>
                </dl>
              </div>
              <QuoteForm preselectedService={service.name} preselectedCity={city.name} compact />
            </div>
          </div>
        </div>
      </Section>

      {/* Same service in nearby cities — critical for internal linking */}
      <Section testid="sc-nearby" className="bg-ink-900 border-y border-white/10">
        <p className="overline mb-4">Same service, nearby cities</p>
        <h2 className="font-display text-3xl md:text-5xl tracking-tightest mb-10">
          {service.name} in cities near {city.name}.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {nearbyCombos.map((n) => (
            <Link
              key={n.slug}
              to={`/${n.slug}`}
              data-testid={`nearby-${n.slug}`}
              className="group bg-ink-950 hover:bg-ink-900 p-6 md:p-8 flex justify-between items-center transition-colors duration-200"
            >
              <span className="font-display text-2xl md:text-3xl tracking-tightest text-bone-50 group-hover:text-copper-500">
                {n.city.name} {service.name}
              </span>
              <ArrowUpRight className="w-5 h-5 text-bone-400 group-hover:text-copper-500 group-hover:rotate-45 transition-transform" />
            </Link>
          ))}
        </div>
      </Section>

      {/* Other services in this city */}
      <Section testid="sc-related">
        <p className="overline mb-4">More work in {city.name}</p>
        <h2 className="font-display text-3xl md:text-5xl tracking-tightest mb-10">
          Other services we run out of {city.name}.
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {SERVICES.filter((sv) => sv.slug !== service.slug).map((sv) => (
            <Link
              key={sv.slug}
              to={`/${scSlug(city.slug, sv.slug)}`}
              className="group bg-ink-950 hover:bg-ink-900 p-5 md:p-6 flex flex-col justify-between min-h-[140px]"
            >
              <span className="font-display text-2xl text-copper-500 leading-none">
                {sv.accent}
              </span>
              <span className="font-heading text-sm md:text-base text-bone-50 group-hover:text-copper-500 mt-6">
                {city.name} {sv.name}
              </span>
            </Link>
          ))}
        </div>
      </Section>
    </>
  );
}
